import { useState } from 'react';
import { Optional } from '@/generic/functional';
import { inject } from '@/rules-management/injections.ts';
import { RULES_REPOSITORY } from '@/rules-management/RulesManagementKeys.ts';
import type { DraftRule } from '@/rules-management/domain/DraftRule.ts';
import { DraftRulesSelection } from '@/rules-management/domain/DraftRulesSelection.ts';
import {
  type DraftRulesPublication,
  publishDraftRules,
} from '@/rules-management/domain/DraftRulesPublication.ts';

const FIRST_DRAFT_RULES_PAGE = 1;

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

export const useDraftRulesPublication = ({
  reloadDrafts,
  onFullyPublished,
}: {
  reloadDrafts: (page: number) => Promise<void>;
  onFullyPublished: () => void;
}) => {
  const [selection, setSelection] = useState(DraftRulesSelection.empty());
  const [publicationFailure, setPublicationFailure] = useState<Optional<DraftRulesPublication>>(
    Optional.empty(),
  );
  const [discardFailure, setDiscardFailure] = useState<Optional<Error>>(Optional.empty());

  const reset = () => {
    setSelection(DraftRulesSelection.empty());
    setPublicationFailure(Optional.empty());
    setDiscardFailure(Optional.empty());
  };

  const reportPublicationFailure = (publication: DraftRulesPublication): Promise<void> => {
    setSelection((current) => current.retain(publication.failed));
    setPublicationFailure(Optional.of(publication));
    return reloadDrafts(FIRST_DRAFT_RULES_PAGE);
  };

  const handlePublish = (): Promise<void> => {
    setPublicationFailure(Optional.empty());
    return publishDraftRules(inject(RULES_REPOSITORY), selection.ids).then((publication) => {
      if (publication.hasPublished) {
        inject(RULES_REPOSITORY).notifyRulesPublished();
        inject(RULES_REPOSITORY).notifyDraftRulesChanged();
      }
      if (!publication.hasFailed) {
        onFullyPublished();
        return;
      }
      return reportPublicationFailure(publication);
    });
  };

  const handleDiscard = (draft: DraftRule, page: number): Promise<void> => {
    setDiscardFailure(Optional.empty());
    return inject(RULES_REPOSITORY)
      .discard(draft.id)
      .then(() => {
        setSelection((current) => current.deselectAll([draft]));
        inject(RULES_REPOSITORY).notifyDraftRulesChanged();
        return reloadDrafts(page);
      })
      .catch((error) => setDiscardFailure(Optional.of(toError(error))));
  };

  return {
    selection,
    setSelection,
    publicationFailure,
    handlePublish,
    discardFailure,
    handleDiscard,
    reset,
  };
};
